import React from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { MainLayout } from '@/components/layouts/MainLayout';
import { RequireAuth } from '@/components/shared/RequireAuth';

const LoginPage = React.lazy(() => import('@/pages/LoginPage'));
const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const DataPage = React.lazy(() => import('@/pages/DataPage'));
const OptionManagementPage = React.lazy(() => import('@/pages/OptionManagementPage'));
const UsersPage = React.lazy(() => import('@/pages/UsersPage'));
const UsersSeminarPage = React.lazy(() => import('@/pages/UsersSeminarPage'));
const AdminsPage = React.lazy(() => import('@/pages/AdminsPage'));
const TransactionsPage = React.lazy(() => import('@/pages/TransactionsPage'));
const SubscriptionsPage = React.lazy(() => import('@/pages/SubscriptionsPage'));
const CoursesPage = React.lazy(() => import('@/pages/CoursesPage'));
const CoursesCategories = React.lazy(() => import('@/pages/CoursesCategories'));
const CoursesTeachers = React.lazy(() => import('@/pages/CoursesTeachers'));
const CoursesOrders = React.lazy(() => import('@/pages/CoursesOrders'));
const CoursesLicense = React.lazy(() => import('@/pages/CoursesLicense'));
const CoursesPosts = React.lazy(() => import('@/pages/CoursesPosts'));
const AnalysisPage = React.lazy(() => import('@/pages/AnalysisPage'));
const TicketsPage = React.lazy(() => import('@/pages/TicketsPage'));
const FaqPage = React.lazy(() => import('@/pages/FaqPage'));
const FinancePage = React.lazy(() => import('@/pages/FinancePage'));
const DiscountsPage = React.lazy(() => import('@/pages/DiscountsPage'));
const FaqCategoriesPage = React.lazy(() => import('@/pages/FaqCategoriesPage'));
const MediaPage = React.lazy(() => import('@/pages/MediaPage'));
const NotificationsPage = React.lazy(() => import('@/pages/NotificationsPage'));
const AgentRagPage = React.lazy(() => import('@/pages/AgentRagPage'));
const AgentRagDocumentsPage = React.lazy(() => import('@/pages/AgentRagDocumentsPage'));
const AgentOverviewPage = React.lazy(() => import('@/pages/AgentOverviewPage'));
const AgentUsersPage = React.lazy(() => import('@/pages/AgentUsersPage'));
const AgentUsagePage = React.lazy(() => import('@/pages/AgentUsagePage'));
const AgentConversationsPage = React.lazy(() => import('@/pages/AgentConversationsPage'));
const AgentSettingsPage = React.lazy(() => import('@/pages/AgentSettingsPage'));
const AiCreditsPage = React.lazy(() => import('@/pages/AiCreditsPage'));
const ReferralsPage = React.lazy(() => import('@/pages/ReferralsPage'));
const CampaignsPage = React.lazy(() => import('@/pages/CampaignsPage'));
const PortfolioPage = React.lazy(() => import('@/pages/PortfolioPage'));
const PortfolioDetailPage = React.lazy(() => import('@/pages/PortfolioDetailPage'));
const PortfolioReportsPage = React.lazy(() => import('@/pages/PortfolioReportsPage'));
const PortfolioSummaryPage = React.lazy(() => import('@/pages/PortfolioSummaryPage'));
const ConsultingPage = React.lazy(() => import('@/pages/ConsultingPage'));
const PortfolioManagementDefinitionsPage = React.lazy(
  () => import('@/pages/PortfolioManagementDefinitionsPage')
);
const PublicSupportInboxPage = React.lazy(() => import('@/pages/PublicSupportInboxPage'));

const PageLoader = () => (
  <div
    className="flex min-h-[320px] items-center justify-center"
    role="status"
    aria-label="در حال بارگذاری صفحه"
  >
    <div className="h-9 w-9 animate-spin rounded-full border-2 border-emerald-100 border-t-emerald-600" />
  </div>
);

const LoginLoader = () => (
  <div
    className="flex min-h-screen items-center justify-center bg-slate-50"
    role="status"
    aria-label="در حال بارگذاری"
  >
    <div className="h-12 w-12 animate-spin rounded-full border-2 border-emerald-100 border-t-emerald-600" />
  </div>
);

const ProtectedLayout = () => (
  <RequireAuth>
    <MainLayout>
      <React.Suspense fallback={<PageLoader />}>
        <Outlet />
      </React.Suspense>
    </MainLayout>
  </RequireAuth>
);

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <React.Suspense fallback={<LoginLoader />}>
        <LoginPage />
      </React.Suspense>
    ),
  },
  {
    element: <ProtectedLayout />,
    children: [
      { path: '/', element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/data', element: <DataPage /> },
      { path: '/options-management', element: <OptionManagementPage /> },
      { path: '/users', element: <UsersPage /> },
      { path: '/users/seminar', element: <UsersSeminarPage /> },
      { path: '/admins', element: <AdminsPage /> },
      { path: '/finance', element: <FinancePage /> },
      { path: '/finance/transactions', element: <TransactionsPage /> },
      { path: '/finance/discounts', element: <DiscountsPage /> },
      { path: '/subscriptions', element: <SubscriptionsPage /> },
      { path: '/courses', element: <CoursesPage /> },
      { path: '/courses/categories', element: <CoursesCategories /> },
      { path: '/courses/teachers', element: <CoursesTeachers /> },
      { path: '/courses/orders', element: <CoursesOrders /> },
      { path: '/courses/license', element: <CoursesLicense /> },
      { path: '/courses/posts', element: <CoursesPosts /> },
      { path: '/analysis', element: <AnalysisPage /> },
      { path: '/tickets', element: <TicketsPage /> },
      { path: '/consulting', element: <ConsultingPage /> },
      { path: '/faq', element: <FaqPage /> },
      { path: '/faq/categories', element: <FaqCategoriesPage /> },
      { path: '/media', element: <MediaPage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '/ai/agent-overview', element: <AgentOverviewPage /> },
      { path: '/ai/agent-users', element: <AgentUsersPage /> },
      { path: '/ai/agent-usage', element: <AgentUsagePage /> },
      { path: '/ai/agent-conversations', element: <AgentConversationsPage /> },
      { path: '/ai/agent-settings', element: <AgentSettingsPage /> },
      { path: '/agent/rag', element: <AgentRagPage /> },
      { path: '/agent/rag/:categoryId', element: <AgentRagDocumentsPage /> },
      { path: '/agent/tokens', element: <Navigate to="/ai-credits" replace /> },
      { path: '/ai-credits', element: <AiCreditsPage /> },
      { path: '/referrals', element: <ReferralsPage /> },
      { path: '/campaigns', element: <CampaignsPage /> },
      {
        path: '/public-support/:conversationId?',
        element: <PublicSupportInboxPage />,
      },
      { path: '/portfolio', element: <PortfolioPage /> },
      { path: '/portfolio/reports', element: <PortfolioReportsPage /> },
      { path: '/portfolio/summary', element: <PortfolioSummaryPage /> },
      { path: '/portfolio/:portfolioId', element: <PortfolioDetailPage /> },
      {
        path: '/portfolio-management',
        element: <PortfolioManagementDefinitionsPage />,
      },
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);
